/**
 * /zh-tw/voice-to-text — 語音轉文字 pillar reframe bridge (Traditional Chinese).
 *
 * Targets 語音轉文字 (20k) / 錄音轉文字 (1.9k). Reframe: 逐字稿是素材，Draftlize
 * 把決策變成活的產品脈絡。currentLocale 'zhtw' → /zh-tw URL, zh-Hant hreflang.
 */
import { MarketingShell } from './MarketingShell';

export function ZhTwVoiceToText() {
  return (
    <MarketingShell
      currentLocale="zhtw"
      hreflangPath="/voice-to-text"
      hreflangLocales={['zhtw']}
      documentTitle="語音轉文字完整指南｜免費把錄音變文字，然後呢？ · Draftlize"
      documentDescription="教你免費把語音、錄音轉成文字，更重要的是下一步——從逐字稿裡萃取出真正推動產品的「決策」。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 語音轉文字</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            語音轉文字——<br />
            <em>逐字稿不是終點。</em>
          </h1>
          <p className="lede">
            把錄音丟進去，幾秒就吐出乾淨的逐字稿。Notta、PLAUD、雅婷逐字稿這類工具，在「把說過的話完整記下來」這件事上真的很強，你可以繼續用。但逐字稿是<em>素材</em>，不是成果。那場會議真正重要的——你們下的<em>決策</em>和理由——會埋在幾千字裡，落進一份沒人再打開的檔案，幾週後就過時。這頁要談的是逐字稿的「下一步」：把會議裡的決策，變成一直保持最新的產品脈絡。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">免費把錄音轉文字，三步驟。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>選擇檔案</strong>——MP3、M4A、WAV 或一段語音訊息。</li>
            <li><strong>進行轉換</strong>——AI 語音轉文字工具把聲音變成文字，多半有免費額度。</li>
            <li><strong>校對</strong>——把人名、專有名詞順過一遍，再匯出。</li>
          </ol>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '22px 0 0' }}>
            語音、錄音、影片的音檔都適用。問題是：逐字稿越準，文字山就越大，那一個關鍵決策也就埋得越深。「記下來」早就不是瓶頸，<em>「抽出來」</em>才是。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">素材 vs 活的脈絡</span>
            <h2 className="serif">逐字稿結束的地方，<em>工作才開始。</em></h2>
          </div>
          <p>你的語音轉文字工具和 Draftlize 做的是不同的事。一個記錄對話，一個讓對話裡的決策一直有效。兩個都留著。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>語音轉文字工具</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>工作</td><td>記錄說了什麼</td><td className="ours yes">讓決定的事一直保持最新</td></tr>
            <tr><td>產出</td><td className="partial">逐字稿＋摘要</td><td className="ours yes">圖譜中的決策卡片</td></tr>
            <tr><td>某個決策推翻前一個</td><td className="no">兩則無關的筆記</td><td className="ours yes">互相連結，舊的標記 stale</td></tr>
            <tr><td>三週後</td><td className="no">埋在檔案裡</td><td className="ours yes">可引用、最新或已標記</td></tr>
            <tr><td>AI agent 能用</td><td className="partial">讀到的是純文字</td><td className="ours yes">透過 MCP 可讀可寫</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">把決策變成<em>活的脈絡。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            記錄交給工具就好。關鍵是文字裡那幾個決策接下來怎麼了——在 Draftlize，它們不再是摘要裡的一行，而成為一張一直為真的圖譜。會議記錄的整理方式也是同一套邏輯，見 <a href="/zh-tw/meeting-minutes-ai" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>AI 會議記錄指南</a>。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">決策變成卡片，不是一行字</h3>
            <p>把逐字稿或摘要貼進來，裡面的決策變成有型別的卡片——決策本身、理由、考慮過的替代方案、負責人。一小時的對話濃縮成「必須活下來」的那幾項，每張都能用 ID 引用。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">新決策讓被推翻的舊決策 stale</h3>
            <p>這週的會議改了上個月的決定。因為彼此連結，新決策會自動把舊的——以及建立在它上面的規格——標記為 stale。下次開會不會再重議已經拍板的事。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 讀得到「決定了什麼」</h3>
            <p>會議的決策成為 Claude Code 或 Cursor 透過 MCP 讀取的脈絡——下一份規格的 agent 一開始就知道團隊週二決定了什麼、為什麼，而不必每次重讀逐字稿。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          工具能把會議完美轉成逐字稿。它就是沒辦法告訴你，三週後裡面哪些決策還算數。
        </blockquote>
        <cite>語音轉文字工具照用。讓決策一直有效的是 Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常見問題</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '有免費的語音轉文字工具嗎？', a: 'Notta、PLAUD、雅婷逐字稿等多半有免費額度，挑順手的用。Draftlize 不做語音轉文字——它在逐字稿出來之後接手，把裡面的決策變成結構化、可保持最新的卡片。' },
            { q: '準確度如何？支援台語、粵語嗎？', a: '近年的 AI 語音轉文字對中文已堪用，台語、粵語的支援因工具而異。但越準的逐字稿文字越多，關鍵決策也埋得越深，所以「抽出決策」這一步才重要。' },
            { q: '轉成文字之後怎麼用？', a: '把摘要貼進 Draftlize，把裡面的決策做成卡片——決策、理由、負責人。重點不是存整份逐字稿，而是讓那幾個必須活過這場會議的決策保持可引用、最新。' },
            { q: '和直接存在筆記軟體裡有什麼差別？', a: '存在筆記裡，決策只是一段與相關規格脫節的純文字，會悄悄過時。Draftlize 把它們連起來，當你改變主意時，自動標出哪些東西因此 stale。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">把逐字稿，變成決策<em>——免費送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新註冊送 $5</span>
          <span>用多少付多少</span>
          <span>餘額永不過期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            語音轉文字工具照用。把摘要貼進 Draftlize，裡面的決策就變成活的卡片——互相連結、保持最新、AI agent 也讀得到。下次開會是往上疊，而不是重來。
          </p>
          <a className="btn-primary" href="/">免費開始，送 $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
